export type UserRole = 'admin' | 'dentist' | 'secretary' | 'comptable';

export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: UserRole;
  roles?: UserRole[];
  all_roles?: UserRole[];
  phone?: string;
  specialite?: string;
  numero_ordre?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateUserData {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: UserRole;
  roles?: UserRole[];
  phone?: string;
  specialite?: string;
  numero_ordre?: string;
  password: string;
  password_confirm?: string;
}

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface AuthResponse {
  token: string;
  user: User;
}

// Types pour les patients
export interface Assureur {
  id: number;
  nom: string;
  contact?: string;
  email?: string;
  telephone?: string;
  adresse?: string;
  taux_couverture: number;
  created_at: string;
}

export interface Patient {
  id: number;
  a_verifier?: boolean;  // dossier créé via WhatsApp, à compléter par l'accueil
  nom: string;
  prenoms: string;
  date_naissance: string;
  genre: 'M' | 'F';
  telephone: string;
  email?: string;
  adresse?: string;
  profession?: string;
  antecedents_medicaux?: string;
  allergies?: string;
  medicaments_actuels?: string;
  assureur?: number;
  assureur_details?: Assureur;
  numero_assure?: string;
  nom_souscripteur?: string;
  photo_carte_assurance?: string;
  age: number;
  created_by?: number;
  created_at: string;
  updated_at: string;
  is_active: boolean;
}

export interface CreatePatientData {
  a_verifier?: boolean;
  nom: string;
  prenoms: string;
  date_naissance?: string;  // Optionnel si age_input est fourni
  age_input?: number;        // Nouvel champ: âge en années (alternative à date_naissance)
  genre: 'M' | 'F';
  telephone: string;
  email?: string;
  adresse?: string;
  profession?: string;
  antecedents_medicaux?: string;
  allergies?: string;
  medicaments_actuels?: string;
  assureur?: number;
  numero_assure?: string;
  nom_souscripteur?: string;
}

export interface DossierMedical {
  id: number;
  patient: number;
  notes_generales?: string;
  created_at: string;
  updated_at: string;
}

export interface DocumentPatient {
  id: number;
  patient: number;
  type_document: 'ordonnance' | 'radio' | 'analyse' | 'autre';
  titre: string;
  fichier: string;
  description?: string;
  uploaded_by?: number;
  uploaded_by_name?: string;
  uploaded_at: string;
}

// Types pour le planning
export interface RendezVous {
  id: number;
  patient: number;
  patient_details?: Patient;
  doctor: number;
  doctor_details?: Doctor;
  date_heure: string;
  duree: string;
  motif: string;
  notes?: string;
  status: 'attente' | 'confirme' | 'realise' | 'annule' | 'non_honore';
  rappel_email_envoye: boolean;
  rappel_sms_envoye: boolean;
  created_by?: number;
  created_at: string;
  updated_at: string;
}

export interface CreateRendezVousData {
  patient: number;
  doctor: number;
  date_heure: string;
  duree?: string;
  motif: string;
  notes?: string;
}

export interface DisponibiliteDoctor {
  id: number;
  doctor: number;
  jour_semaine: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  heure_debut: string;
  heure_fin: string;
  is_active: boolean;
}

// Types pour les consultations
export type CategorieSoin = '' | 'conservateurs' | 'protheses' | 'orthodontie' | 'chirurgie' | 'esthetique' | 'enfants';

export const CATEGORIES_SOIN: Array<{ value: CategorieSoin; label: string }> = [
  { value: '', label: 'Non classé' },
  { value: 'conservateurs', label: 'Soins conservateurs' },
  { value: 'protheses', label: 'Prothèses' },
  { value: 'orthodontie', label: 'Orthodontie' },
  { value: 'chirurgie', label: 'Chirurgie' },
  { value: 'esthetique', label: 'Esthétique' },
  { value: 'enfants', label: 'Soins pour enfants' },
];

export interface CreateTreatmentTypeData {
  name: string;
  description?: string;
  base_price: number;
  duration_minutes: number;
  // Catalogue présenté sur WhatsApp
  categorie?: CategorieSoin;
  description_patient?: string;
  prix_max?: number | null;
  visible_whatsapp?: boolean;
  non_facturable?: boolean;
}

export interface Consultation {
  id: number;
  patient: number;
  patient_details?: Patient;
  doctor: number;
  doctor_details?: Doctor;
  rendez_vous?: number;
  date_consultation: string;
  motif_consultation: string;
  examen_clinique?: string;
  diagnostic?: string;
  plan_traitement?: string;
  notes?: string;
  dents_concernees?: string;
  odontogramme?: OdontogrammeState | null;
  is_locked: boolean;
  sans_facturation?: boolean;          // clôturée sans facture (suite de soins)
  facture?: number | null;
  treatments: TreatmentRealise[];
  ordonnances: Ordonnance[];
  created_at: string;
  updated_at: string;
}

// ---- Odontogramme interactif ----
export type ToothFace = 'M' | 'D' | 'O' | 'V' | 'L';
export type ToothSurfaceState = 'carie' | 'plombage';
export type ToothWholeState =
  | 'sain'
  | 'couronne'
  | 'implant'
  | 'absente'
  | 'extraire'
  | 'endo'
  | 'coiffage';

export interface ToothState {
  whole: ToothWholeState;
  surfaces: Record<ToothFace, ToothSurfaceState | null>;
}

export interface Bridge {
  teeth: string[];
}

export interface OdontogrammeState {
  dentition: 'adulte' | 'lait';
  teeth: Record<string, ToothState>;
  bridges: Bridge[];
}

export interface Treatment {
  id: number;
  patient: number;
  patient_details?: Patient;
  doctor: number;
  doctor_details?: Doctor;
  treatment_type: number;
  treatment_type_details?: TreatmentType;
  appointment_date: string;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  notes: string;
  actual_price: number;
  created_at: string;
  updated_at: string;
}

export interface Doctor {
  id: number;
  user: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    username: string;
  };
  user_details?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    username: string;
  };
  speciality: string;
  license_number: string;
  phone: string;
}

export interface ConfigRappelTraitement {
  id: number;
  treatment_type: number;
  actif: boolean;
  delai_mois: number;
  delai_jours: number;
  duree_rdv_minutes: number;
  motif_template: string;
  motif_rendu: string;
  priorite: 'low' | 'normal' | 'high';
  envoyer_notif_creation: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateConfigRappelData {
  treatment_type: number;
  actif: boolean;
  delai_mois: number;
  delai_jours: number;
  duree_rdv_minutes: number;
  motif_template: string;
  priorite: 'low' | 'normal' | 'high';
  envoyer_notif_creation: boolean;
}

export interface TreatmentType {
  id: number;
  name: string;
  description: string;
  base_price: number;
  duration_minutes: number;
  categorie?: CategorieSoin;
  description_patient?: string;
  prix_max?: number | null;
  visible_whatsapp?: boolean;
  non_facturable?: boolean;            // suite de soins : prix 0, jamais facturée
  config_rappel?: ConfigRappelTraitement | null;
}

export interface TreatmentRealise {
  id: number;
  consultation: number;
  treatment_type: number;
  treatment_details?: TreatmentType;
  quantite: number;
  prix_unitaire: number;
  montant_total: number;
  notes?: string;
}

export interface Medicament {
  id: number;
  nom: string;
  forme: string;
  dosage_standard: string;
  indication?: string;
  contre_indication?: string;
  is_active: boolean;
  created_at: string;
}

export interface OrdonnanceMedicament {
  id: number;
  ordonnance: number;
  medicament: number;
  medicament_details?: Medicament;
  dosage: string;
  frequence: string;
  duree: string;
  instructions?: string;
  quantite?: string;
}

export interface Ordonnance {
  id: number;
  consultation: number;
  contenu?: string;
  notes?: string;
  medicaments: OrdonnanceMedicament[];
  created_at: string;
}

// Types pour la facturation
export interface Facture {
  id: number;
  numero: string;
  patient: number;
  patient_details?: Patient;
  consultation?: number;
  consultation_details?: Consultation;
  date_emission: string;
  date_echeance: string;
  date_paiement_min?: string;          // Premier jour où un paiement est accepté
  montant_actes: number;
  montant_supplementaire: number;
  montant_total: number;
  pourcentage_remise: number;          // Nouveau: pourcentage de remise (0-100)
  montant_remise: number;              // Nouveau: montant de la remise
  pourcentage_assurance: number;
  montant_assurance: number;
  montant_patient: number;
  status: 'brouillon' | 'emise' | 'payee' | 'partiellement_payee' | 'prise_en_charge_assurance' | 'annulee';
  is_locked: boolean;                  // Nouveau: verrouillage après paiement
  notes?: string;
  lignes: LigneFacture[];
  paiements: Paiement[];
  montant_paye?: number;               // Calculé côté serveur
  montant_restant?: number;            // Calculé côté serveur
  created_at: string;
  updated_at: string;
}

export interface LigneFacture {
  id: number;
  facture: number;
  acte_realise: number;
  quantite: number;
  prix_unitaire: number;
  montant: number;
}

export interface Paiement {
  id: number;
  facture: number;
  montant: number;
  mode_paiement: string;  // Modes de paiement récupérés dynamiquement du backend
  date_paiement: string;
  reference?: string;
  notes?: string;
  created_at: string;
}

// ==================== Types pour les factures Pro Forma ====================

export type StatutProforma = 'brouillon' | 'envoyee' | 'acceptee' | 'refusee' | 'convertie' | 'expiree' | 'annulee';

export interface LigneProforma {
  id: number;
  proforma: number;
  description: string;
  quantite: number;
  prix_unitaire: number;
  montant: number;
  treatment_type?: number;
  treatment_type_name?: string;
}

export interface FactureProforma {
  id: number;
  numero: string;
  patient: number;
  patient_details?: Patient;
  consultation?: number;
  consultation_details?: Consultation;
  date_creation: string;
  date_validite: string;
  montant_actes: number;
  montant_supplementaire: number;
  montant_total: number;
  pourcentage_remise: number;
  montant_remise: number;
  pourcentage_assurance: number;
  montant_assurance: number;
  montant_patient: number;
  status: StatutProforma;
  objet?: string;
  notes?: string;
  conditions?: string;
  lignes: LigneProforma[];
  est_valide?: boolean;
  peut_etre_converti?: boolean;
  facture_convertie?: number;
  facture_convertie_numero?: string;
  date_conversion?: string;
  created_by?: number;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
}

export interface FactureProformaList {
  id: number;
  numero: string;
  patient: number;
  patient_nom: string;
  date_creation: string;
  date_validite: string;
  montant_total: number;
  montant_patient: number;
  status: StatutProforma;
  objet?: string;
  est_valide?: boolean;
  jours_restants?: number;
  facture_convertie?: number;
}

export interface CreateProformaData {
  patient: number;
  consultation?: number;
  date_validite: string;
  montant_actes: number;
  montant_supplementaire?: number;
  montant_total: number;
  pourcentage_remise?: number;
  montant_remise?: number;
  pourcentage_assurance?: number;
  montant_assurance?: number;
  montant_patient: number;
  objet?: string;
  notes?: string;
  conditions?: string;
  lignes: Array<{
    description: string;
    quantite: number;
    prix_unitaire: number;
    treatment_type?: number;
  }>;
}

export interface ConvertProformaData {
  date_echeance: string;
  notes?: string;
}

export interface ProformaStatistiques {
  total_proformas: number;
  proformas_mois: number;
  en_attente: number;
  acceptees: number;
  converties: number;
  expirees: number;
  montant_total_mois: number;
  taux_conversion: number;
}

export interface PatientStats {
  patients_by_month: Array<{ month: string; count: number }>;
  insurance_stats: Array<{ insurance_company__name: string; count: number }>;
  insurance_coverage: { with_insurance: number; without_insurance: number };
  total_patients: number;
}

export interface FinancialStats {
  invoiced_by_month: Array<{ month: string; total: number }>;
  paid_by_month: Array<{ month: string; total: number }>;
  payment_methods: Array<{ payment_method: string; total: number; count: number }>;
  outstanding_amount: number;
}

// Types pour le stock
export interface Fournisseur {
  id: number;
  nom: string;
  contact?: string;
  telephone?: string;
  email?: string;
  adresse?: string;
  type_fournisseur?: string;
  type_display?: string;
  delai_paiement_jours?: number;
  rib?: string;
  solde_du?: string | number;
  is_active: boolean;
}

export interface CategorieIntrant {
  id: number;
  nom: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateCategorieIntrantData {
  nom: string;
  description?: string;
}

export interface Intrant {
  id: number;
  code: string;
  nom: string;
  description?: string;
  categorie: number;
  categorie_details?: CategorieIntrant;
  unite: string;
  unite_display?: string;
  seuil_critique: number;
  seuil_commande: number;
  duree_vie?: number;
  is_active: boolean;
  stock_actuel: number;
  est_en_rupture: boolean;
  est_critique: boolean;
  created_at: string;
}

export interface LotIntrant {
  id: number;
  intrant: number;
  numero_lot: string;
  date_peremption?: string;
  quantite_initiale: number;
  quantite_restante: number;
  fournisseur?: number;
  fournisseur_details?: Fournisseur;
  date_reception: string;
  prix_unitaire?: number;
  est_perime: boolean;
}

export interface MouvementStock {
  id: number;
  intrant: number;
  intrant_details?: Intrant;
  lot?: number;
  lot_details?: LotIntrant;
  type_mouvement: 'entree' | 'sortie';
  motif: 'achat' | 'livraison' | 'consommation' | 'perte' | 'peremption' | 'ajustement';
  quantite: number;
  date_mouvement: string;
  reference_document?: string;
  consultation?: number;
  notes?: string;
  created_by?: number;
  created_by_details?: User;
  created_at: string;
}

export interface TreatmentStats {
  top_treatments: Array<{
    name: string;
    revenue: number;
    count: number;
    average_price: number;
  }>;
  doctor_stats: Array<{
    doctor_name: string;
    speciality: string;
    treatments: number;
    revenue: number;
  }>;
  treatments_by_month: Array<{
    month: string;
    count: number;
  }>;
}

export interface CrossAnalysisStats {
  revenue_per_patient: number;
  occupancy_rate: number;
  productivity_by_doctor: Array<{
    doctor_name: string;
    patients_per_day: number;
    revenue_per_day: number;
  }>;
  seasonal_trends: Array<{
    month: string;
    patients: number;
    revenue: number;
  }>;
  insurance_profitability: Array<{
    insurance_name: string;
    avg_treatment_value: number;
    payment_delay: number;
    profit_margin: number;
  }>;
}

export interface ExecutiveKPIs {
  monthly_summary: {
    patients_this_month: number;
    revenue_this_month: number;
    payment_rate: number;
    appointments_today: number;
    growth_rate: number;
  };
  alerts: Array<{
    type: 'warning' | 'error' | 'info';
    message: string;
    value: number;
    threshold: number;
  }>;
  top_performers: {
    best_doctor: { name: string; revenue: number };
    best_treatment: { name: string; count: number };
    best_insurance: { name: string; coverage: number };
  };
  financial_overview: {
    total_revenue_ytd: number;
    total_outstanding: number;
    avg_collection_time: number;
    profit_margin: number;
  };
}

// Types pour les API responses
export interface PaginatedResponse<T> {
  count: number;
  next?: string;
  previous?: string;
  results: T[];
}

export interface ApiError {
  message: string;
  status: number;
  data?: any;
}

// Types pour les paramètres de requête
export interface QueryParams {
  search?: string;
  page?: number;
  page_size?: number;
  ordering?: string;
  [key: string]: any;
}

// Types pour les composants
export interface FormErrors {
  [key: string]: string;
}

export interface SelectOption {
  value: string | number;
  label: string;
}

// Types pour le dashboard
export interface DashboardStats {
  totalPatients: number;
  rdvAujourdhui: number;
  facturesEnAttente: number;
  montantFacturesEnAttente: number;
  stockCritique: number;
  revenus30Jours: number;
  croissancePatients: number;
  consultationsCompletees: number;
}

export interface ChartData {
  name: string;
  value: number;
  color?: string;
  montant?: number;
}

export interface RdvChartData {
  name: string;
  rendezVous: number;
}

export interface RevenueChartData {
  name: string;
  montant: number;
  color?: string;
}

// Types pour les communications
export interface MessageTemplate {
  id: number;
  name: string;
  template_type: 'appointment_confirmation' | 'appointment_reminder' | 'appointment_cancellation' | 
                 'treatment_followup' | 'payment_reminder' | 'welcome_patient' | 'birthday_wishes';
  template_type_display: string;
  subject: string;
  content: string;
  email_enabled: boolean;
  sms_enabled: boolean;
  whatsapp_enabled: boolean;
  is_active: boolean;
  created_at: string;
}

export interface CommunicationLog {
  id: number;
  patient: number;
  patient_details: Patient;
  communication_type: 'email' | 'sms' | 'whatsapp' | 'call';
  communication_type_display: string;
  template?: number;
  template_name?: string;
  recipient: string;
  subject: string;
  message: string;
  status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed' | 'bounced';
  status_display: string;
  sent_at?: string;
  delivered_at?: string;
  read_at?: string;
  cost?: number;
  provider_message_id?: string;
  error_message?: string;
  created_by: number;
  created_at: string;
}

export interface NotificationPreference {
  id: number;
  patient: number;
  patient_details: Patient;
  appointment_reminders_email: boolean;
  appointment_reminders_sms: boolean;
  appointment_reminders_whatsapp: boolean;
  payment_reminders_email: boolean;
  payment_reminders_sms: boolean;
  marketing_emails: boolean;
  birthday_wishes: boolean;
  preferred_language: string;
  timezone: string;
  preferred_contact_time_start: string;
  preferred_contact_time_end: string;
  updated_at: string;
}

export interface SendMessageData {
  patient_id: number;
  template_id: number;
  communication_type: 'email' | 'sms' | 'whatsapp';
  custom_subject?: string;
  custom_message?: string;
  context_data?: Record<string, any>;
}

export interface BulkMessageData {
  patient_ids: number[];
  template_id: number;
  communication_type: 'email' | 'sms' | 'whatsapp';
  context_data?: Record<string, any>;
}

export interface CommunicationStats {
  total_messages: number;
  emails_sent: number;
  sms_sent: number;
  whatsapp_sent: number;
  success_rate: number;
  total_cost: number;
  failed_messages: number;
  pending_messages: number;
}

// Prosthesis types
export * from './prosthesis';

// ==================== Types pour les dépenses ====================

export type TypeCategorieDepense =
  | 'fournisseur'
  | 'materiel'
  | 'salaire'
  | 'loyer'
  | 'utilities'
  | 'maintenance'
  | 'transport'
  | 'communication'
  | 'assurance'
  | 'impots'
  | 'marketing'
  | 'formation'
  | 'divers';

export type StatutDepense = 'en_attente' | 'approuvee' | 'payee' | 'annulee';

export type ModePaiementDepense = 'especes' | 'carte' | 'cheque' | 'virement' | 'mobile';

export type PrioriteDepense = 'basse' | 'normale' | 'haute' | 'urgente';

export type FrequenceRecurrence = 'mensuelle' | 'trimestrielle' | 'annuelle';

export interface CategorieDepense {
  id: number;
  nom: string;
  type_categorie: TypeCategorieDepense;
  description?: string;
  couleur: string;
  icone?: string;
  is_active: boolean;
  budget_mensuel?: number;
  compte_charge?: number | null;
  compte_charge_numero?: string | null;
  compte_charge_intitule?: string | null;
  total_depenses_mois?: number;
  pourcentage_budget?: number;
  nombre_depenses?: number;
  created_at: string;
  updated_at: string;
}

export interface CategorieDepenseSimple {
  id: number;
  nom: string;
  type_categorie: TypeCategorieDepense;
  couleur: string;
}

export interface Depense {
  id: number;
  numero: string;
  categorie: number;
  categorie_details?: CategorieDepenseSimple;
  description: string;
  montant: number;
  date_depense: string;
  date_echeance?: string;
  date_paiement?: string;
  statut: StatutDepense;
  mode_paiement?: ModePaiementDepense;
  priorite: PrioriteDepense;
  fournisseur?: number;
  fournisseur_details?: {
    id: number;
    nom: string;
    telephone?: string;
    email?: string;
  };
  reference_facture?: string;
  reference_paiement?: string;
  justificatif?: string;
  notes?: string;
  motif_annulation?: string;
  nature: NatureDepense;
  impacte_caisse: boolean;
  est_recurrente: boolean;
  frequence_recurrence?: FrequenceRecurrence;
  created_by?: number;
  created_by_name?: string;
  approved_by?: number;
  approved_by_name?: string;
  est_en_retard?: boolean;
  jours_avant_echeance?: number;
  created_at: string;
  updated_at: string;
}

export interface DepenseList {
  id: number;
  numero: string;
  description: string;
  montant: number;
  date_depense: string;
  date_echeance?: string;
  statut: StatutDepense;
  priorite: PrioriteDepense;
  categorie: number;
  categorie_nom: string;
  categorie_couleur: string;
  fournisseur_nom?: string;
  est_en_retard?: boolean;
  mode_paiement?: ModePaiementDepense;
  nature: NatureDepense;
  impacte_caisse: boolean;
}

/** Journalière : passe par la caisse du jour. Mensuelle : réglée hors caisse
 *  journalière (salaires, factures fournisseurs). */
export type NatureDepense = 'journaliere' | 'mensuelle';

export interface CreateDepenseData {
  categorie: number;
  description: string;
  montant: number;
  date_depense: string;
  date_echeance?: string;
  priorite?: PrioriteDepense;
  fournisseur?: number;
  reference_facture?: string;
  notes?: string;
  est_recurrente?: boolean;
  frequence_recurrence?: FrequenceRecurrence;
  nature?: NatureDepense;
  impacte_caisse?: boolean;
}

/** Situation des disponibilités (caisse espèces + Mobile Money) sur une période */
export interface SituationCanal {
  encaissements: number;
  decaissements: number;
  decaissements_depenses: number;
  decaissements_autres: number;
  net_periode: number;
  solde_initial: number;
  solde_dernier_folio: number;
  date_dernier_folio: string | null;
  nombre_folios: number;
}

export interface SituationCaisse {
  periode: { date_debut: string; date_fin: string; personnalisee: boolean };
  especes: SituationCanal;
  mobile_money: SituationCanal;
  total: {
    encaissements: number;
    decaissements: number;
    net_periode: number;
    disponible: number;
  };
  hors_caisse: {
    total: number;
    nombre: number;
    par_categorie: { categorie: string; montant: number }[];
    par_mode: { mode: string; montant: number }[];
  };
  detail_jours: {
    date: string;
    especes: { encaissements: number; decaissements: number; solde: number };
    mobile_money: { encaissements: number; decaissements: number; solde: number };
  }[];
}

/** Recette nette : encaissements espèces + Mobile Money, moins les dépenses
 *  payées sur la période (journalières et mensuelles) */
export interface RecetteNetteMois {
  mois: string;                       // "2026-06"
  libelle: string;                    // "juin 2026"
  recettes_especes: number;
  recettes_mobile_money: number;
  recettes_total: number;
  depenses_journalieres: number;
  depenses_mensuelles: number;
  depenses_total: number;
  recette_nette: number;
}

export interface RecetteNette {
  periode: { date_debut: string; date_fin: string; personnalisee: boolean };
  recettes: {
    especes: number;
    especes_soins: number;
    especes_autres: number;
    mobile_money: number;
    mobile_money_soins: number;
    mobile_money_autres: number;
    total: number;
  };
  depenses: {
    journalieres: number;
    mensuelles: number;
    total: number;
    sur_caisse: number;
    hors_caisse: number;
    nombre: number;
    par_categorie: { categorie: string; montant: number }[];
    a_payer: { montant: number; nombre: number };
  };
  recette_nette: number;
  taux_marge: number;
  mois: RecetteNetteMois[];
}

export interface BudgetMensuel {
  id: number;
  mois: string;
  budget_total: number;
  notes?: string;
  total_depenses?: number;
  solde_restant?: number;
  pourcentage_utilise?: number;
  created_by?: number;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
}

export interface PaiementFournisseurDepense {
  id: number;
  fournisseur: number;
  fournisseur_details?: {
    id: number;
    nom: string;
    telephone?: string;
    email?: string;
  };
  depense?: number;
  montant: number;
  date_paiement: string;
  mode_paiement: ModePaiementDepense;
  reference?: string;
  notes?: string;
  created_by?: number;
  created_by_name?: string;
  created_at: string;
}

export interface DepenseStatistiques {
  total_mois: number;
  total_annee: number;
  nombre_depenses_mois: number;
  nombre_en_attente: number;
  nombre_en_retard: number;
  montant_en_attente: number;
  budget_mensuel?: number;
  solde_budget?: number;
  pourcentage_budget?: number;
  depenses_par_categorie: Array<{
    categorie__nom: string;
    categorie__couleur: string;
    total: number;
    count: number;
  }>;
  evolution_mensuelle: Array<{
    mois: string;
    mois_court: string;
    total: number;
  }>;
  top_fournisseurs: Array<{
    fournisseur__nom: string;
    total: number;
    count: number;
  }>;
}

export interface PayerDepenseData {
  mode_paiement: ModePaiementDepense;
  reference_paiement?: string;
  date_paiement?: string;
}